import { isEqual } from 'lodash';
import { TARGET_TYPES } from 'app/forms/config/unified/constants';
import {
  POLICY_APPLICATIONS,
  SEVERITY_MAP,
  EVENT_POLICY_TYPES,
  ALERT_MANAGER_ACTIVATION_MODES,
  ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY
} from '@kentik/ui-shared/alerting/constants';
import {
  isSyslogEventPolicy,
  isSnmpTrapEventPolicy,
  getDefaultEventAppProtoFilter
} from 'app/stores/alerting/policyUtils';
import { defaultSerializer } from 'app/util/nms/serializations';

const DEFAULT_KEVENT_POLICY = {
  application: POLICY_APPLICATIONS.KEVENT,
  metrics: ['fps'],
  clearOverride: null,
  evaluationPeriod: '0s',
  nTopKeysEvaluated: '0',
  nTopKeysAddedToBaseline: '0',
  minTrafficValue: 0,
  baselineSettings: {
    datapointAggregationMethod: 'REL_P25',
    startOffset: '86400s',
    length: '518400s',
    minimalLength: '86400s',
    seasonality: 'hourly',
    neighbourhoodAggregationMethod: 'None',
    neighbourhoodRadius: 0,
    comparisonValueAggregateFunction: 'REL_P95',
    weekendAware: true
  }
};

const DEFAULT_KEVENT_CONDITION = {
  metric: 'fps',
  operator: 'greaterThanOrEquals',
  comparisonValue: 0,
  type: 'static',
  direction: 'thresholdDirectionNone',
  fallbackSettings: null,
  keySettings: null,
  ratioSettings: null
};

const serializeActivationSettings = (policy) => {
  const { activationSettings = {} } = policy || {};

  return {
    ...activationSettings,
    mode: ALERT_MANAGER_ACTIVATION_MODES.TOGGLE
  };
};

const serializeThresholdFilters = (filters = {}) => {
  const transformedConjunctions = (filters?.conjunctions || []).map((conjunction = {}) => ({
    ...conjunction,
    entries: (conjunction?.entries || []).map((entry = {}) => {
      if (Array.isArray(entry.patterns) && entry?.patterns?.filter(Boolean)?.length > 0) {
        delete entry.values;
      } else {
        delete entry.patterns;
      }
      return entry;
    })
  }));

  // isInclude determines whether filter matches (true) or excludes (false) traffic.
  // If it is omitted, it will be set to true by default.
  return { ...filters, conjunctions: transformedConjunctions, isInclude: true };
};

const serializeThresholds = (policy) => {
  const { thresholds } = policy || {};

  if (!thresholds || thresholds.length === 0) {
    return [];
  }

  // Use of dupe `enabled` properties is a holdover until we rewrite PolicySummaryPanel
  return thresholds
    .filter((t) => t.uiProperties?.enabled || t.enabled)
    .map(({ enabled, ...threshold }) => ({
      ...threshold,
      conditions: [DEFAULT_KEVENT_CONDITION],
      filters: serializeThresholdFilters(threshold.filters)
    }));
};

const serializeFilterGroups = (policy) => {
  if (policy.filters?.filterGroups?.length > 0) {
    return policy.filters.filterGroups;
  }

  // Unless a user provides one, we use a default filter to improve performance
  const defaultAppProtoFilter = getDefaultEventAppProtoFilter(policy.dimensions);

  return [{ connector: 'all', filters: [defaultAppProtoFilter] }];
};

const deserializeFilterGroups = (policy) => {
  if (!Array.isArray(policy.filters?.filterGroups)) {
    return [];
  }

  if (policy.filters.filterGroups.length !== 1) {
    return policy.filters.filterGroups;
  }

  // If no policy filter is provided at creation, we add our own "timestamp > 0" filter.
  // This reduces the data processed by a policy by scoping it to the right AppProtocol.
  // However, we don't want this filter showing to users, or being applied to chart queries.

  const appProtoFilter = getDefaultEventAppProtoFilter(policy.dimensions);

  return policy.filters.filterGroups.filter(({ filters }) => {
    const hasOneFilter = filters.length === 1;
    const hasTimestampFilter = isEqual(filters[0], appProtoFilter);
    // Only reject the filter group if it has a single filter, and that filter is the appProtoFilter
    return !(hasOneFilter && hasTimestampFilter);
  });
};

const deserializeThresholds = (policy) => {
  const { thresholds, rule = {} } = policy || {};

  if (!thresholds || thresholds.length === 0) {
    return [
      {
        uiProperties: {
          enabled: true
        },
        enabled: true, // This is a holdover until we rewrite PolicySummaryPanel
        severity: SEVERITY_MAP.MAJOR.STANDARD_ALERT,
        direction: 'currentToHistory',
        conditions: [],
        ackRequired: false,
        filters: null,
        mitigations: [],
        notificationChannels: []
      }
    ];
  }

  return thresholds.map((threshold) => {
    const ruleLevel = (rule.levels || []).find(
      (level) => ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY[level.severity] === threshold.severity
    );

    const deserializedThreshold = {
      ...threshold,
      enabled: true, // This is a holdover until we rewrite PolicySummaryPanel
      uiProperties: { enabled: true }
    };

    if (ruleLevel) {
      deserializedThreshold.ackRequired = ruleLevel.ack_required;
    }

    return deserializedThreshold;
  });
};

const deserializeEventType = (policy) => {
  const { dimensions = [] } = policy || {};

  if (isSnmpTrapEventPolicy(dimensions)) {
    return EVENT_POLICY_TYPES.SNMP_TRAP;
  }

  if (isSyslogEventPolicy(dimensions)) {
    return EVENT_POLICY_TYPES.SYSLOG;
  }

  // Redundant, but prefer being verbose
  return EVENT_POLICY_TYPES.SYSLOG;
};

const deserialize = (apiPolicy) => {
  if (!apiPolicy) {
    return {};
  }

  return {
    ...apiPolicy,
    application: POLICY_APPLICATIONS.KEVENT,
    metrics: ['fps'],
    thresholds: deserializeThresholds(apiPolicy),
    filters: {
      connector: apiPolicy.filters?.connector || 'all',
      filterGroups: deserializeFilterGroups(apiPolicy)
    },
    uiProperties: {
      targetType: TARGET_TYPES.EVENT,
      eventType: deserializeEventType(apiPolicy)
    }
  };
};

const serialize = (policyAttrs) => {
  if (!policyAttrs) {
    return {};
  }

  return defaultSerializer(
    {
      ...DEFAULT_KEVENT_POLICY,
      ...policyAttrs,
      filters: {
        connector: policyAttrs.filters?.connector || 'all',
        filterGroups: serializeFilterGroups(policyAttrs)
      },
      thresholds: serializeThresholds(policyAttrs),
      activationSettings: serializeActivationSettings(policyAttrs)
    },
    ['uiProperties']
  );
};

export { deserialize, serialize };
